import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getApiBase } from '../utils/apiBase';
import { t } from '../locales';

const API_URL = getApiBase() + '/api';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordAgain, setNewPasswordAgain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const tok = (sp.get('token') || '').trim();
      if (tok) {
        setToken(tok);
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        const qs = url.searchParams.toString();
        const next = `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`;
        window.history.replaceState({}, '', next);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!token) {
      setStatus('invalid');
      setErrorMessage(t('auth.reset.toastInvalidLink'));
      toast.error(t('auth.reset.toastInvalidLink'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('auth.reset.toastPasswordMin'));
      return;
    }
    if (newPassword !== newPasswordAgain) {
      toast.error(t('auth.reset.toastMismatch'));
      return;
    }
    setSubmitting(true);
    setStatus('idle');
    setErrorMessage('');
    try {
      await axios.post(
        `${API_URL}/auth/reset-password`,
        { token, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(t('auth.reset.toastUpdated'));
      setNewPassword('');
      setNewPasswordAgain('');
      setStatus('success');
    } catch (error) {
      const msg = error.response?.data?.detail || t('auth.reset.resetFailedFallback');
      const text = Array.isArray(msg) ? msg.join(', ') : String(msg);
      const lower = text.toLowerCase();
      if (lower.includes('geçersiz')) {
        setStatus('invalid');
      } else if (lower.includes('süresi dolmuş')) {
        setStatus('expired');
      } else {
        setStatus('invalid');
      }
      setErrorMessage(text);
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: 'radial-gradient(circle at center, #2d1b4e 0%, #050505 70%)' }}
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[min(100%,22rem)]">
        <div className="glass-effect rounded-2xl p-6 sm:p-7 border border-white/5 shadow-xl shadow-black/40">
          <div className="text-center mb-6">
            <p
              className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              {t('common.brand')}
            </p>
            <h1
              className="text-2xl sm:text-[1.65rem] font-bold text-white leading-tight"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              {t('auth.reset.subtitle')}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <Label htmlFor="new-password" className="text-gray-300">
                {t('auth.reset.labelNew')}
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="mt-1 h-11 bg-black/50 border-white/20 text-white"
                placeholder={t('auth.login.passwordDots')}
                data-testid="reset-new-password"
              />
            </div>
            <div>
              <Label htmlFor="new-password-again" className="text-gray-300">
                {t('auth.reset.labelNewAgain')}
              </Label>
              <Input
                id="new-password-again"
                type="password"
                value={newPasswordAgain}
                onChange={(e) => setNewPasswordAgain(e.target.value)}
                required
                className="mt-1 h-11 bg-black/50 border-white/20 text-white"
                placeholder={t('auth.login.passwordDots')}
                data-testid="reset-new-password-again"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !token}
              className="w-full btn-primary"
              data-testid="reset-password-submit"
            >
              {submitting ? t('auth.reset.submitLoading') : t('auth.reset.submit')}
            </Button>
          </form>

          {status === 'success' ? (
            <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
              {t('auth.reset.successBanner')}
              <Button type="button" className="w-full mt-3 btn-primary" onClick={() => navigate('/login')}>
                {t('auth.reset.goToLogin')}
              </Button>
            </div>
          ) : null}
          {status === 'invalid' ? (
            <p className="text-center mt-4 text-sm text-red-400 break-words px-0.5" data-testid="reset-password-error">
              {errorMessage || t('auth.reset.invalidLinkFallback')}
            </p>
          ) : null}
          {status === 'expired' ? (
            <p
              className="text-center mt-4 text-sm text-amber-300 break-words px-0.5"
              data-testid="reset-password-error"
            >
              {errorMessage || t('auth.reset.expiredFallback')}
            </p>
          ) : null}
          {!token ? (
            <p className="text-center mt-4 text-sm text-red-400 break-words px-0.5">{t('auth.reset.noToken')}</p>
          ) : null}

          <p className="text-center mt-6 text-sm text-gray-400">
            <Link to="/login" className="text-violet-400 hover:text-violet-300 font-semibold">
              {t('auth.reset.backToLogin')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
